import { EmptyFileState } from '@/components/shared/files/EmptyFileState';
import { FileList } from '@/components/shared/files/FileList';
import { FilePreview } from '@/components/shared/files/FilePreview';
import { CenteredSpinner } from '@/components/shared/Spinner';
import { SignerForm, SignerItem } from '@/components/dashboard/signatures';
import { AddPackageFilesForm } from '@/components/dashboard/signatures/AddPackageFilesForm';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FileType } from '@/hooks/queries/useFileQueries';
import { useSignatureQueries } from '@/hooks/queries/useSignatureQueries';
import { toast } from "@/hooks/useToast";
import { UploadIcon } from '@radix-ui/react-icons';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Mail, Plus } from "lucide-react";
import { Suspense, useState } from 'react';

export const Route = createFileRoute('/_protected/dashboard/signatures/$packageId')({
  component: PackageDetailViewWrapper,
});

function PackageDetailViewWrapper() {
  const { packageId } = Route.useParams();
  return <PackageDetailView packageId={packageId} />;
}

function PackageDetailView({ packageId }: { packageId: string }) {
  const navigate = useNavigate();

  const { useFetchPackage, removeSignerMutation, removeFileMutation, deletePackageMutation, updatePackageMutation } = useSignatureQueries();

  const [isSignerFormOpen, setIsSignerFormOpen] = useState(false);
  const [isPackageFileFormOpen, setIsPackageFileFormOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileType | null>(null);

  const { data: pkg } = useFetchPackage(packageId);

  const IS_DRAFT = pkg.status === 'draft';

  const handleDeleteSigner = async (signerId: string) => {
    if (window.confirm('Are you sure you want to remove this signer?')) {
      await removeSignerMutation.mutateAsync({
        signerId,
        packageId
      }, {
        onSuccess: () => {
          toast({
            title: 'Signer removed',
            description: 'Signer has been removed from the package.',
          });
        },
        onError: (error) => {
          console.error('Error removing signer:', error);
          toast({
            title: 'Failed to remove signer',
            description: error.message || 'There was an error removing the signer.',
            variant: 'destructive',
          });
        }
      });
    }
  };

  const handleRemoveFile = async (packageFileId: string) => {
    if (window.confirm('Are you sure you want to remove this file from the package?')) {
      await removeFileMutation.mutateAsync({
        packageFileId,
        packageId
      }, {
        onSuccess: () => {
          if (selectedFile?.file_id === packageFileId) {
            setSelectedFile(null);
          }
          toast({
            title: 'File removed',
            description: 'File has been removed from the package.',
          });
        },
        onError: (error) => {
          console.error('Error removing file:', error);
          toast({
            title: 'Failed to remove file',
            description: error.message || 'There was an error removing the file.',
            variant: 'destructive',
          });
        }
      });
    }
  };

  const handleDeletePackage = async () => {
    if (window.confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
      await deletePackageMutation.mutateAsync({
        packageId
      }, {
        onSuccess: () => {
          toast({
            title: 'Package deleted',
            description: 'Signature package has been deleted.',
          });
          navigate({ to: '/dashboard/signatures' });
        },
        onError: (error) => {
          console.error('Error deleting package:', error);
          toast({
            title: 'Failed to delete package',
            description: error.message || 'There was an error deleting the package. Please try again.',
            variant: 'destructive',
          });
        }
      });
    }
  };

  const handleSendPackage = async () => {
    if (pkg.signers.length === 0) {
      toast({
        title: 'No signers assigned',
        description: 'Please add at least one signer before sending this package.',
        variant: 'destructive',
      });
      return;
    }

    if (pkg.package_files.length === 0) {
      toast({
        title: 'No files added',
        description: 'Please add at least one file to the package before sending.',
        variant: 'destructive',
      });
      return;
    }

    await updatePackageMutation.mutateAsync({
      packageId, updates: { status: 'sent' }
    }, {
      onSuccess: () => {
        toast({
          title: 'Package sent',
          description: 'The signature package has been sent to the signers.',
        });
      },
      onError: (error) => {
        console.error('Error sending package:', error);
        toast({
          title: 'Failed to send package',
          description: 'There was an error sending the package. Please try again.',
          variant: 'destructive',
        });
      }
    });
  };

  const handleCancelPackage = async () => {
    if (window.confirm('Are you sure you want to cancel this package?')) {
      await updatePackageMutation.mutateAsync({
        packageId, updates: { status: 'cancelled' }
      }, {
        onSuccess: () => {
          toast({
            title: 'Package canceled',
            description: 'The signature package has been canceled.',
          });
        },
        onError: (error) => {
          console.error('Error canceling package:', error);
          toast({
            title: 'Failed to cancel package',
            description: 'There was an error canceling the package. Please try again.',
            variant: 'destructive',
          });
        }
      });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
      <Card className="flex flex-col h-full overflow-hidden col-span-1">
        <CardHeader>
          <CardTitle>{pkg.name}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden flex flex-col space-y-2">
          <>
            {/* Signers Section */}
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium flex items-center">
                Signers
              </h3>
              {IS_DRAFT && (
                <div>
                  {pkg.signers.length === 1 ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      Multi-signer support coming soon
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setIsSignerFormOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Signer
                    </Button>
                  )}
                  <SignerForm
                    packageId={packageId}
                    open={isSignerFormOpen}
                    setOpen={setIsSignerFormOpen}
                  />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2 overflow-auto">
              {!pkg.signers || pkg.signers.length === 0 ? (
                <div className="text-sm text-muted-foreground p-3 border rounded-md">
                  No signers added yet. Add signers to send this package for signature.
                </div>
              ) : (
                [...pkg.signers].reverse().map((signer) => (
                  <SignerItem
                    key={signer.signer_id}
                    signer={signer}
                    onDelete={IS_DRAFT ? () => handleDeleteSigner(signer.signer_id) : undefined}
                  />
                ))
              )}
            </div>
            <Separator />
            {/* Files Section */}

            <div className="flex-1 space-y-2 overflow-auto">
              <FileList
                title="Document Files"
                files={pkg.package_files.map((pkgFile) => pkgFile.file)}
                selectedFile={selectedFile}
                onSelectFile={setSelectedFile}
                onDeleteFile={IS_DRAFT ? (file: FileType) => {
                  const pkgFileId = pkg.package_files.find((pkgFile) => pkgFile.file.file_id == file.file_id)
                  if (pkgFileId) {
                    handleRemoveFile(pkgFileId.package_file_id)
                  }
                } : undefined}
                onAddFiles={IS_DRAFT ? () => setIsPackageFileFormOpen(true) : undefined}
                addButtonLabel="Select Files"
                addButtonIcon={<UploadIcon className="h-4 w-4 mr-2" />}
                emptyMessage="No files Selected yet. Click 'Select Files' to Select your documents."
              />
              <AddPackageFilesForm
                packageId={packageId}
                open={isPackageFileFormOpen}
                setOpen={setIsPackageFileFormOpen}
              />
            </div>
          </>
        </CardContent>
        <CardFooter className="flex justify-between">
          {pkg.status === 'sent' && (
            <Button variant="destructive" onClick={handleCancelPackage}>
              Cancel Package
            </Button>
          )}

          {pkg.status === 'draft' && (
            <>
              <Button
                variant="destructive"
                onClick={handleDeletePackage}
              >
                Delete
              </Button>
              <Button onClick={handleSendPackage}>
                <Mail className="h-4 w-4 mr-2" /> Send
              </Button>
            </>
          )}
        </CardFooter>
      </Card>

      <Card className="h-full max-h-full col-span-1 lg:col-span-2 overflow-hidden">
        {selectedFile ? (
          <Suspense fallback={<CenteredSpinner />}>
            <FilePreview file={selectedFile} />
          </Suspense>
        ) : (
          <EmptyFileState />
        )}
      </Card>
    </div>
  );
}